// The venue and organisation sidebars (spec/13 §2, design guide): the sections, grouped by job, and which a member
// sees. **Only what is built, and only to a role that may open it** — as HQ's sidebar (`hq/sections.ts`): a section
// is listed when the member holds at least one of its permissions, read from `GET /v1/me/access` — the permissions
// each screen's own reads are gated by; the server checks every request again (spec/13 §1). A section not built yet
// is not listed at all — its `/soon/{id}` placeholder route stays for an old link (`ComingSoon`). The id is the icon
// key (`nav-icons.tsx`) and the label's (`shell.nav.<id>`).
//
// Sessions and Wallet were sections until 2026-10-07: sessions are run from the Floor, a player's wallet from their
// Wallet tab in Players; their old links redirect (`org/routes.tsx`). The permissions are written as codes, as HQ's
// are; `src/__tests__/nav-sections.test.tsx` checks each is one the contract has.

import type { Access } from '../api/queries';

export interface SectionSpec {
  readonly id: string;
  /** Listed to a member holding any of these. */
  readonly permissions: readonly string[];
  /**
   * Listed only where this is on for the venue, as well (`visibleGroups`' `features`): `stock` is the venue's stock
   * tracking — on (`inventory.enabled`), or off but the member may turn it on, and then the page says how.
   */
  readonly feature?: 'stock' | 'boot';
  /**
   * Listed only where the organisation has Clavian Stations (`MyAccess.products`): the sections about sessions, players and
   * selling time. A Boot-only venue is a floor of PCs with Clavian Boot, and does not list them.
   */
  readonly product?: 'stations';
}

export interface GroupSpec {
  readonly id: string;
  readonly sections: readonly SectionSpec[];
}

/** Settings is a screen of groups, each with its own permission: reading them, or the printers' or tax's alone. */
const SETTINGS_PERMISSIONS = ['settings:read', 'settings:hardware', 'tax:manage'];
/** Pricing & promotions: its tabs are passes, the rate card and promotions, each read on its own permission. */
const PRICING_PERMISSIONS = ['passes:read', 'pricing:read', 'discounts:read'];

export const VENUE_GROUPS: readonly GroupSpec[] = [
  {
    id: 'operations',
    sections: [
      { id: 'floor', permissions: ['floor:read'] },
      { id: 'sell', permissions: ['orders:create'], product: 'stations' },
      { id: 'orders', permissions: ['orders:read'], product: 'stations' },
      { id: 'kitchen', permissions: ['kitchen:read'], product: 'stations' },
      // Stock (spec/13 §6.4, ADR-0040): only where the venue tracks stock — or, off, to a member who can turn it on.
      { id: 'stock', permissions: ['inventory:read'], feature: 'stock', product: 'stations' },
      // Reports (spec/17): Games played, then the money reports (F-CAT-04, F-ANL-03).
      { id: 'reports', permissions: ['reports:read'], product: 'stations' },
    ],
  },
  {
    id: 'people',
    sections: [
      { id: 'players', permissions: ['players:read'], product: 'stations' },
      { id: 'shifts', permissions: ['shifts:own', 'shifts:read_all', 'shifts:day_close'], product: 'stations' },
      { id: 'staff', permissions: ['members:read'] },
    ],
  },
  {
    id: 'setup',
    sections: [
      // Pricing & promotions (spec/13 §18): passes, the rate, promotions, codes and coupons. Named so
      // it is not taken for HQ's game catalogue (the product owner, 2026-09-29).
      { id: 'pricing', permissions: PRICING_PERMISSIONS, product: 'stations' },
      { id: 'library', permissions: ['library:read'], product: 'stations' },
      { id: 'stations', permissions: ['stations:read'] },
      // Clavian Boot (spec/25 §16): only where the organisation has the add-on — the layout asks the api, which answers 402 without it.
      { id: 'boot', permissions: ['boot:view'], feature: 'boot' },
      { id: 'settings', permissions: SETTINGS_PERMISSIONS },
    ],
  },
];

/**
 * The organisation sidebar (spec/13 §2's second list): what is built of it. The label of a section whose name differs
 * from the venue's (Staff and roles) reads from its own key.
 */
export const ORG_GROUPS: readonly GroupSpec[] = [
  {
    id: 'operations',
    sections: [
      { id: 'venues', permissions: ['venues:read'] },
      { id: 'players', permissions: ['players:read'], product: 'stations' },
    ],
  },
  {
    id: 'commerce',
    sections: [{ id: 'pricing', permissions: PRICING_PERMISSIONS, product: 'stations' }],
  },
  {
    id: 'setup',
    sections: [
      { id: 'staffAndRoles', permissions: ['members:read'] },
      // What the organisation pays for add-ons, read-only (F-BOOT-17).
      { id: 'addons', permissions: ['org:billing'] },
      { id: 'settings', permissions: SETTINGS_PERMISSIONS },
      { id: 'audit', permissions: ['audit:read'] },
    ],
  },
];

/** Which venue features are on for the member here: a section that names one is listed only when it is. */
export type SectionFeatures = Readonly<Partial<Record<NonNullable<SectionSpec['feature']>, boolean>>>;

/**
 * The sections a member may open, by group, in the sidebar's order; a group left empty is dropped. While the access
 * is read nothing is listed (it fails closed, as HQ's does). If it cannot be read at all, every built section is
 * listed: each screen still asks the server and says so when the role may not. A section that depends on a venue
 * feature (`feature`) is listed only when `features` says it is on.
 */
export function visibleGroups(specs: readonly GroupSpec[], access: Pick<Access, 'status' | 'can'> & Partial<Pick<Access, 'products'>>, features: SectionFeatures = {}): readonly GroupSpec[] {
  if (access.status === 'loading') return [];
  const allowed = (section: SectionSpec) =>
    (section.feature === undefined || features[section.feature] === true) &&
    (section.product !== 'stations' || access.products?.stations !== false) &&
    (access.status === 'unavailable' || section.permissions.some((permission) => access.can(permission)));
  return specs.map((group) => ({ ...group, sections: group.sections.filter(allowed) })).filter((group) => group.sections.length > 0);
}
